// src/app/core/interceptors/backend-token.interceptor.ts
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationService } from '@/services/notification.service';
import { inject } from '@angular/core';

const API_TOKEN_KEY = 'token_backend';

/**
 * Interceptor que:
 * 1) Só toca em requisições do backend (securityApiBase em dev ou domínio de prod).
 * 2) Ignora a rota de troca de token (/azure-token).
 * 3) Lê o ApiToken do localStorage e anexa nos headers:
 *    - Authorization: Bearer <token>
 *    - token_backend: <token>   (tolerância para backends que usam esse header)
 * 4) Em 401/403, limpa token e redireciona para /login?error=expired
 * 5) Em falha de rede (status === 0), redireciona para /login?error=network
 */
export const backendTokenInterceptor: HttpInterceptorFn = (
    req: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
    const notify = inject(NotificationService);
    // ---- 0) Nunca interceptar a rota de troca do Azure token
    if (req.url.endsWith('/azure-token')) return next(req);

    // ---- 1) Detectar se a URL é do nosso backend
    const devBase = environment.securityApiBase?.replace(/\/$/, ''); // sem barra final
    const apiBase = environment.apiBase?.replace(/\/$/, ''); // apiBase também é backend
    const prodBase = 'https://api.lojinhalibbs.com.br/api';
    const isBackendUrl = (url: string) =>
        (devBase && url.startsWith(devBase)) ||
        (apiBase && url.startsWith(apiBase)) ||
        url.startsWith(prodBase) ||
        url.includes('/SegUsuario/') ||
        url.includes('/Comprador/');

    if (!isBackendUrl(req.url)) {
        // Não é backend -> segue sem mexer
        return next(req);
    }

    // ---- 2) Ler token do localStorage e normalizar "Bearer "
    let bearer: string | null = null;
    if (typeof window !== 'undefined') {
        bearer = localStorage.getItem(API_TOKEN_KEY);
        if (bearer) bearer = bearer.replace(/^Bearer\s+/i, ''); // remove prefixo duplicado se existir
    }

    // ---- 3) Clonar requisição com headers se houver token
    if (bearer) {
        req = req.clone({
            setHeaders: {
                Authorization: `Bearer ${bearer}`,
                token_backend: bearer, // compat com backends que ainda leem esse header
            },
        });
    }

    // ---- 4) Encaminhar e tratar erros de auth/rede
    return next(req).pipe(
        catchError((error: unknown) => {

            // ---------- 1) Garantir que é um erro HTTP válido ----------
            const err = error as any;
            const status: number | undefined = typeof err?.status === 'number'
                ? err.status
                : undefined;

            const isBrowser = typeof window !== 'undefined';
            const isNavigatorAvailable = typeof navigator !== 'undefined';

            const isOffline = isNavigatorAvailable && navigator.onLine === false;
            const isNetworkError = status === 0;

            // Evita múltiplos redirects simultâneos
            const safeRedirect = (url: string) => {
                if (!isBrowser) return;

                // evita redirecionar se já estiver na página
                if (window.location.pathname !== url) {
                    window.location.replace(url);
                }
            };

            // ---------- 2) AUTHENTICATION (401) ----------
            if (status === 401) {
                try {
                    localStorage.removeItem(API_TOKEN_KEY);
                    localStorage.removeItem('token_backend_expiration');
                } catch { }

                safeRedirect('/login?error=expired');

                return throwError(() => error);
            }

            // ---------- 3) AUTHORIZATION (403) ----------
            if (status === 403) {
                safeRedirect('/auth/sem-permissao');
                return throwError(() => error);
            }

            // ---------- 4) NETWORK ----------
            if (isNetworkError) {
                if (isOffline) {
                    notify.warning('Sem conexão com a internet.');
                } else {
                    notify.error('Erro ao conectar com o servidor.');
                }

                return throwError(() => error);
            }

            // ---------- 5) CONFLICT ----------
            if (status === 409) {
                const message =
                    typeof err?.error?.message === 'string'
                        ? err.error.message
                        : 'Já existe um registro com esta informação.';

                notify.warning(message);
                return throwError(() => error);
            }

            // ---------- 6) OUTROS ERROS HTTP ----------
            if (status && status >= 500) {
                const message =
                    typeof err?.error?.message === 'string'
                        ? err.error.message
                        : 'Ocorreu um erro inesperado.';

                notify.error(message);
                return throwError(() => error);
            }

            if (status === 400) {
                return throwError(() => error);
            }

            // ---------- 7) ERRO DESCONHECIDO ----------
            notify.error('Erro inesperado na aplicação.');
            return throwError(() => error);
        })
    );
};
